/* EUSU NON-SELLING STOCK (2026-10-26; Max: "đưa file này lên portal", with the folder "EUSU report" — the dashboard
   somebody rebuilt every week by dropping the workbook in a folder and running build_dashboard.py; he chose a screen
   of its own). The workbook is uploaded here, every "Stock list MMDD" sheet becomes a stored week, and the server works
   out the dashboard with the script's own rules (eusuStock.js). The three pages are the dashboard's: Overview, By root
   cause, WoW change. Nothing is computed here — the screen only arranges what the server sends.
   SINCE 2026-10-29 the week is the warehouse's "Helinox Inventory Status For Finished Goods" .xls (Max: "đổi format upload
   cho file EUSU"): the server checks each item code against the product database and fills the root cause and the price
   from memory; what it could not settle is listed above the tabs (NeedsFix), where an editor sets it. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileSpreadsheet, Link2, FileCode2, Copy } from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar, LabelList,
} from 'recharts';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import { BACKEND_URL } from './backendConfig';
import { useTheme } from './ThemeContext';
import { SERIES, OTHER_COLOR } from './explorerModel';
import './SPHub.css';
import './ProjectApproval.css';
import './InventoryReport.css';
import './EusuStock.css';

const API = `${BACKEND_URL}/api/eusu-stock`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayLabel = (d) => (d ? `${d.slice(8, 10)}/${MON[Number(d.slice(5, 7)) - 1]}/${d.slice(0, 4)}` : '');
const label = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : '');
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');
const usd = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usd0 = (v) => `$${Math.round(Number(v) || 0).toLocaleString('en-US')}`;
const usdK = (v) => { const x = Math.abs(Number(v) || 0); return x >= 1e6 ? `$${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `$${(x / 1e3).toFixed(0)}K` : `$${Math.round(x)}`; };
const signed = (v, f) => (v > 0 ? `+${f(v)}` : v < 0 ? `−${f(-v)}` : f(0));
const fmtWhen = (v) => { if (!v) return ''; const d = new Date(v); if (Number.isNaN(d.getTime())) return String(v); const vn = new Date(d.getTime() + 7 * 3600 * 1000); return `${vn.getUTCDate()}/${MON[vn.getUTCMonth()]}/${vn.getUTCFullYear()} ${vn.toISOString().slice(11, 16)}`; };

/* Sort a list by one key, blanks last, the way every table here sorts. */
const sortBy = (rows, key, dir) => [...rows].sort((a, b) => {
  const x = a[key]; const y = b[key];
  if (typeof x === 'number' || typeof y === 'number') return dir * ((Number(x) || 0) - (Number(y) || 0));
  return dir * String(x || '').localeCompare(String(y || ''));
});
const SortTh = ({ k, label, sort, setSort, num }) => (
  <th className={`${num ? 'num ' : ''}eu-sort`} onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}
    aria-sort={sort.key === k ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
    {label}<span className="eu-arrow">{sort.key === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}</span>
  </th>
);

/* A delta: up is MORE non-selling stock, which is the bad direction here — red; down green. The sign and the arrow
   say it too, so the colour is never the only cue. */
const Delta = ({ v, f }) => (
  <span className={v > 0 ? 'eu-up' : v < 0 ? 'eu-dn' : 'eu-nc'}>{v > 0 ? '▲ ' : v < 0 ? '▼ ' : ''}{v ? f(Math.abs(v)) : '—'}</span>
);

const Donut = ({ rows, valueKey, colorOf, fmt, total, mode }) => (
  <div className="eu-donut">
    <div className="eu-donut-chart">
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={rows} dataKey={valueKey} nameKey="cause" innerRadius="55%" outerRadius="88%" paddingAngle={0}
            stroke={mode === 'dark' ? '#1b1b1b' : '#ffffff'} strokeWidth={2} isAnimationActive={false}>
            {rows.map((r) => <Cell key={r.cause} fill={colorOf(r.cause)} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${fmt(v)} (${total ? ((v / total) * 100).toFixed(1) : 0}%)`, n]} />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <ul className="eu-legend">
      {rows.map((r) => (
        <li key={r.cause}><i style={{ background: colorOf(r.cause) }} /><span>{r.cause}</span>
          <b>{fmt(r[valueKey])}</b><em>{total ? `${((r[valueKey] / total) * 100).toFixed(1)}%` : ''}</em></li>
      ))}
    </ul>
  </div>
);

const Trend = ({ rows, valueKey, color, fmt, tick }) => (
  <ResponsiveContainer width="100%" height={230}>
    <LineChart data={rows} margin={{ top: 26, right: 40, left: 6, bottom: 0 }}>
      <CartesianGrid vertical={false} stroke="var(--portal-border)" />
      <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
      {/* The axis follows the figures, not zero: a stock that moves 7,267 → 6,048 drawn from 0 is a flat line pressed
          against the top, with its labels on the axis ticks. The labels on the points carry the values. */}
      <YAxis tick={{ fontSize: 11 }} tickFormatter={tick} width={58} axisLine={false} tickLine={false} tickCount={5}
        domain={[(lo) => Math.max(0, lo - (lo * 0.12 || 1)), (hi) => hi + (hi * 0.08 || 1)]} />
      <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l, p) => (p && p[0] ? dayLabel(p[0].payload.date) : l)} />
      <Line type="linear" dataKey={valueKey} stroke={color} strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false}>
        <LabelList dataKey={valueKey} position="top" formatter={tick} style={{ fontSize: 10, fill: 'var(--portal-text-muted)' }} />
      </Line>
    </LineChart>
  </ResponsiveContainer>
);

const CauseBars = ({ rows, valueKey, colorOf, fmt }) => (
  <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 34 + 20)}>
    <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 70, left: 8, bottom: 4 }}>
      <XAxis type="number" hide />
      <YAxis type="category" dataKey="cause" width={170} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
      <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: 'rgba(127,127,127,.08)' }} />
      <Bar dataKey={valueKey} radius={[0, 4, 4, 0]} barSize={18} isAnimationActive={false}>
        {rows.map((r) => <Cell key={r.cause} fill={colorOf(r.cause)} />)}
        <LabelList dataKey={valueKey} position="right" formatter={fmt} style={{ fontSize: 11, fill: 'var(--portal-text)' }} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

/* THE GUIDE (Max 01/11: every module has one). The upload button and the week picker only exist for those who may use
   them / once a week is stored, so TourGuide skips those steps for everybody else. It walks the Overview tab. */
const TOUR = [
  { selector: '.eu-week', title: 'tourEusu.weekT', body: 'tourEusu.weekB' },
  { selector: '[data-tour="eusu-upload"]', title: 'tourEusu.uploadT', body: 'tourEusu.uploadB' },
  { selector: '.eu-page .view-tabs', title: 'tourEusu.tabsT', body: 'tourEusu.tabsB' },
  { selector: '.eu-page .iv-tiles', title: 'tourEusu.tilesT', body: 'tourEusu.tilesB' },
  { selector: '.eu-page .eu-grid2', title: 'tourEusu.chartsT', body: 'tourEusu.chartsB' },
];

export default function EusuStock() {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  const mode = resolved === 'dark' ? 'dark' : 'light';
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState('');
  const [week, setWeek] = useState('');
  const [tab, setTab] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return ['overview', 'cause', 'wow'].includes(q) ? q : 'overview';
  });
  const [flash, setFlash] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  /* THE SHARE LINK — a secret URL that always shows the latest uploaded week (eusuStock.js). Editors only. */
  const [share, setShare] = useState(null); // null = closed; { url, busy, copied }
  const shareCall = async (method) => {
    setShare((x) => ({ ...(x || {}), busy: true, err: '' }));
    try {
      const r = await fetch(`${API}/share`, { method, credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setShare({ url: j.url, busy: false, copied: false });
    } catch (e) { setShare((x) => ({ ...(x || {}), busy: false, err: e.message })); }
  };
  const openShare = () => { setShare({ url: null, busy: true }); shareCall('GET'); };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(share.url); } catch (e) {
      const ta = document.createElement('textarea'); ta.value = share.url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); ta.remove();
    }
    setShare((x) => ({ ...x, copied: true }));
  };
  const fileRef = useRef(null);
  const pending = useRef(null);
  const seq = useRef(0);

  const load = useCallback(async (w) => {
    const my = ++seq.current;
    setErr('');
    try {
      const r = await fetch(`${API}${w ? `?week=${encodeURIComponent(w)}` : ''}`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (my === seq.current) setResp(j);
    } catch (e) { if (my === seq.current) setErr(e.message); }
  }, []);
  useEffect(() => { load(week); }, [load, week]);
  useEffect(() => {
    const u = new URL(window.location.href); u.searchParams.set('tab', tab);
    window.history.replaceState({}, '', u.pathname + u.search);
  }, [tab]);

  const d = resp?.data || null;
  const colorOf = useMemo(() => {
    const order = resp?.causeOrder || [];
    const pal = SERIES[mode];
    return (c) => { const i = order.indexOf(c); return i >= 0 && i < pal.length ? pal[i] : OTHER_COLOR[mode]; };
  }, [resp, mode]);

  /* ---- upload: read first, show what it will do, then save ---- */
  const [linkInv, setLinkInv] = useState(false);
  const send = async (file, commit) => {
    const r = await fetch(`${API}/upload?file=${encodeURIComponent(file.name)}&commit=${commit ? 1 : 0}${commit && linkInv ? '&inventory=1' : ''}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j;
  };
  const onPick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true); setFlash(''); setErr('');
    try {
      pending.current = f;
      const j = await send(f, false);
      // a Friday's file goes to the Global Inventory Report (the old Inventory Working File) too (Max 29/10); another day's only when ticked
      setLinkInv(!!(j.link && j.link.friday));
      setPreview(j);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  const onSave = async () => {
    if (!pending.current) return;
    setBusy(true);
    try {
      const j = await send(pending.current, true);
      setPreview(null); pending.current = null;
      setFlash([t('xEusu.saved', { n: j.weeks.length, file: j.file }),
        j.linked ? t('xEusu.linked', { day: dayLabel(j.linked.day), n: j.linked.rows }) : '',
        j.linkError ? t('xEusu.linkFailed', { e: j.linkError }) : ''].filter(Boolean).join(' '));
      if (week) setWeek(''); else load('');
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main iv-page eu-page">
        <div className="eu-head">
          <h2 className="pa-title">{t('xEusu.title')}</h2>
          <div className="eu-headr">
            {resp?.weeks?.length > 0 && (
              <label className="iv-field eu-week"><span>{t('xEusu.week')}</span>
                <select value={week || (d ? d.date : '')} onChange={(e) => setWeek(e.target.value)}>
                  {resp.weeks.map((w) => <option key={w.date} value={w.date}>{dayLabel(w.date)} · {w.sheet}</option>)}
                </select>
              </label>
            )}
            {d && (
              <>
                {/* The week on screen as an Excel file, and as the team's own HTML page to keep or mail. */}
                <a className="pa-btn eu-upload" href={`${API}/export.xlsx?week=${d.date}`} download>
                  <FileSpreadsheet size={14} /> {t('xEusu.exportXlsx')}
                </a>
                <a className="pa-btn eu-upload" href={`${API}/dashboard.html?week=${d.date}`} download title={t('xEusu.htmlHint')}>
                  <FileCode2 size={14} /> {t('xEusu.downloadHtml')}
                </a>
              </>
            )}
            {resp?.canUpload && d && (
              <button type="button" className="pa-btn eu-upload" onClick={openShare}><Link2 size={14} /> {t('xEusu.shareLink')}</button>
            )}
            {resp?.canUpload && (
              <>
                <button type="button" className="pa-btn primary eu-upload" data-tour="eusu-upload" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}
                  title={t('xEusu.uploadHint')}>
                  <Upload size={14} /> {busy && !preview ? t('xEusu.reading') : t('xEusu.upload')}
                </button>
                <input ref={fileRef} type="file" accept=".xls,.xlsx" hidden onChange={onPick} />
              </>
            )}
            <ModuleHelp steps={TOUR} tourKey="eusuStock" ready={!!resp} onBeforeTour={() => setTab('overview')} />
          </div>
        </div>
        {resp?.lastUpload && (
          <p className="eu-sub">{t('xEusu.lastUpload', { file: resp.lastUpload.file, by: resp.lastUpload.by || '—', at: fmtWhen(resp.lastUpload.at) })}</p>
        )}
        {flash && <div className="eu-flash ok">{flash}</div>}
        {err && <div className="eu-flash bad">{err}</div>}

        {resp && !d && (
          <div className="pa-section eu-empty"><b>{t('xEusu.empty')}</b>{resp.canUpload && <span>{t('xEusu.emptyUpload')}</span>}</div>
        )}

        {d && <NeedsFix d={d} t={t} canEdit={!!resp?.canUpload} causes={resp?.causeOrder || []} onSaved={() => load(week)} />}

        {d && (
          <>
            <div className="pa-tabs view-tabs" role="tablist">
              {['overview', 'cause', 'wow'].map((k) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} className={`pa-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>
                  <TabIcon k={k} />{t(`xEusu.tab${k === 'overview' ? 'Overview' : k === 'cause' ? 'Cause' : 'Wow'}`)}
                </button>
              ))}
            </div>
            {tab === 'overview' && <Overview d={d} t={t} colorOf={colorOf} mode={mode} />}
            {tab === 'cause' && <ByCause d={d} t={t} colorOf={colorOf} />}
            {tab === 'wow' && <Wow d={d} t={t} colorOf={colorOf} />}
          </>
        )}
      </div>

      {share && (
        <div className="eu-overlay" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setShare(null); }}>
          <div className="eu-modal eu-share">
            <div className="eu-modal-head">
              <b>{t('xEusu.shareTitle')}</b>
              <button type="button" className="eu-x" onClick={() => setShare(null)} aria-label={t('xEusu.close')} title={t('xEusu.close')}><X size={18} /></button>
            </div>
            <p className="eu-sub">{t('xEusu.shareSub')}</p>
            <div className="eu-modal-body">
              {share.url ? (
                <div className="eu-linkrow">
                  <input readOnly value={share.url} onFocus={(e) => e.target.select()} aria-label={t('xEusu.shareLink')} />
                  <button type="button" className="pa-btn primary" onClick={copyLink}><Copy size={14} /> {share.copied ? t('xEusu.copied') : t('xEusu.copy')}</button>
                  <a className="pa-btn" href={share.url} target="_blank" rel="noreferrer">{t('xEusu.open')}</a>
                </div>
              ) : (!share.busy && <p className="eu-nolink">{t('xEusu.noLink')}</p>)}
              <p className="eu-warn">{t('xEusu.shareWarn')}</p>
              {share.err && <div className="eu-flash bad">{share.err}</div>}
            </div>
            <div className="eu-modal-foot">
              {share.url && <button type="button" className="pa-btn" disabled={share.busy} onClick={() => shareCall('DELETE')}>{t('xEusu.switchOff')}</button>}
              <button type="button" className="pa-btn primary" disabled={share.busy} onClick={() => shareCall('POST')}>{share.url ? t('xEusu.replaceLink') : t('xEusu.makeLink')}</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="eu-overlay" role="dialog" aria-modal="true">
          <div className="eu-modal">
            <div className="eu-modal-head">
              <b>{t('xEusu.previewTitle', { file: preview.file })}</b>
              <button type="button" className="eu-x" onClick={() => { setPreview(null); pending.current = null; }} aria-label={t('xEusu.close')} title={t('xEusu.close')}><X size={18} /></button>
            </div>
            <p className="eu-sub">{preview.checks ? t('xEusu.invPreviewSub', { n: preview.weeks[0]?.rows || 0, zero: preview.checks.zero }) : t('xEusu.previewSub', { n: preview.weeks.length })}</p>
            <div className="eu-modal-body">
              <table className="pa-table eu-tt">
                <thead><tr><th>{t('xEusu.week')}</th><th>Sheet</th><th className="num">{t('xEusu.rows')}</th><th className="num">{t('xEusu.qty')}</th><th className="num">{t('xEusu.amount')}</th><th /></tr></thead>
                <tbody>
                  {preview.weeks.map((w) => (
                    <tr key={w.date}>
                      <td>{dayLabel(w.date)}</td><td>{w.sheet}</td><td className="num">{w.rows}</td><td className="num">{n0(w.qty)}</td><td className="num">{usd(w.amount)}</td>
                      <td><span className={`eu-st eu-st-${w.status}`}>{t(`xEusu.st${w.status === 'new' ? 'New' : w.status === 'replace' ? 'Replace' : 'Same'}`)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.checks && <InvChecks c={preview.checks} t={t} />}
              {preview.link && (
                <label className="eu-linkinv" data-eu-linkinv>
                  <input type="checkbox" checked={linkInv} onChange={(e) => setLinkInv(e.target.checked)} />
                  <span><b>{t('xEusu.linkInv', { day: dayLabel(preview.link.day) })}</b><br />
                    <em className="eu-sub">{preview.link.friday ? t('xEusu.linkInvFri') : t('xEusu.linkInvOther')}</em></span>
                </label>
              )}
              {preview.skipped.length > 0 && (
                <details className="eu-skipped">
                  <summary>{t('xEusu.skippedN', { n: preview.skipped.length })} — {t('xEusu.skippedWhy')}</summary>
                  <ul>{preview.skipped.map((s) => <li key={s.sheet}>{s.sheet}{s.date ? ` (${dayLabel(s.date)})` : ''}: {s.why}</li>)}</ul>
                </details>
              )}
            </div>
            <div className="eu-modal-foot">
              <button type="button" className="pa-btn" onClick={() => { setPreview(null); pending.current = null; }}>{t('xEusu.cancel')}</button>
              <button type="button" className="pa-btn primary" disabled={busy} onClick={onSave}>{t('xEusu.save', { n: preview.weeks.length })}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Overview({ d, t, colorOf, mode }) {
  const [sort, setSort] = useState({ key: 'amount', dir: -1 });
  const prev = d.prevLabel;
  const rows = sortBy(d.causes, sort.key, sort.dir);
  return (
    <div className="pa-section">
      <p className="eu-sub">{dayLabel(d.date)} · {t('xEusu.sheet', { s: d.sheet })}</p>
      <div className="iv-tiles">
        <div className="iv-tile"><span>{t('xEusu.kQty')}</span><b>{n0(d.totalQty)}</b>
          <em>{prev ? <><Delta v={d.wowQty} f={n0} /> {t('xEusu.vsPrev', { d: prev })}</> : t('xEusu.noPrev')}</em></div>
        <div className="iv-tile"><span>{t('xEusu.kAmt')}</span><b>{usd0(d.totalAmount)}</b>
          <em>{prev ? <><Delta v={d.wowAmount} f={usdK} /> {t('xEusu.vsPrev', { d: prev })}</> : t('xEusu.noPrev')}</em></div>
        <div className="iv-tile"><span>{t('xEusu.kSkus')}</span><b>{n0(d.skuCount)}</b><em>{t('xEusu.kSkusSub')}</em></div>
        <div className="iv-tile"><span>{t('xEusu.kCauses')}</span><b>{d.causes.length}</b><em>{t('xEusu.kCausesSub')}</em></div>
      </div>
      <div className="eu-grid2">
        <div className="eu-card"><h4>{t('xEusu.qtyByCause')}</h4>
          <Donut rows={d.causes} valueKey="qty" colorOf={colorOf} fmt={n0} total={d.totalQty} mode={mode} /></div>
        <div className="eu-card"><h4>{t('xEusu.amtByCause')}</h4>
          <Donut rows={d.causes} valueKey="amount" colorOf={colorOf} fmt={usd0} total={d.totalAmount} mode={mode} /></div>
      </div>
      <div className="eu-grid2">
        <div className="eu-card"><h4>{t('xEusu.trendQty')}</h4>
          <Trend rows={d.trend} valueKey="qty" color={SERIES[mode][0]} fmt={n0} tick={n0} /></div>
        <div className="eu-card"><h4>{t('xEusu.trendAmt')}</h4>
          <Trend rows={d.trend} valueKey="amount" color={SERIES[mode][0]} fmt={usd0} tick={usdK} /></div>
      </div>
      <div className="eu-card">
        <h4>{t('xEusu.causeSummary')}</h4>
        <div className="iv-wrap">
          <table className="pa-table eu-tt">
            <thead><tr>
              <SortTh k="cause" label={t('xEusu.cause')} sort={sort} setSort={setSort} />
              <SortTh k="qty" label={t('xEusu.qty')} sort={sort} setSort={setSort} num />
              <SortTh k="amount" label={t('xEusu.amount')} sort={sort} setSort={setSort} num />
              <th className="num">{t('xEusu.share')}</th>
              <SortTh k="skus" label={t('xEusu.skus')} sort={sort} setSort={setSort} num />
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.cause}>
                  <td><i className="eu-sw" style={{ background: colorOf(c.cause) }} />{c.cause}</td>
                  <td className="num">{n0(c.qty)}</td><td className="num">{usd(c.amount)}</td>
                  <td className="num">{d.totalAmount ? `${((c.amount / d.totalAmount) * 100).toFixed(1)}%` : '—'}</td>
                  <td className="num">{c.skus}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td>{t('xEusu.total')}</td><td className="num">{n0(d.totalQty)}</td><td className="num">{usd(d.totalAmount)}</td><td className="num">100%</td><td className="num">{d.skuCount}</td></tr></tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

/* What the server could not settle when it read the inventory file (eusuStock.js) — told in the preview before saving. */
const FLAG_KEYS = ['code', 'name', 'cause', 'price'];
function InvChecks({ c, t }) {
  const k = c.counts || {};
  return (
    <div className="eu-checks" data-eu-checks>
      <ul>
        {FLAG_KEYS.filter((f) => k[f]).map((f) => <li key={f} className="bad">{t(`xEusu.flagN_${f}`, { n: k[f] })}</li>)}
        {!FLAG_KEYS.some((f) => k[f]) && <li className="ok">{t('xEusu.flagNone')}</li>}
        {k.rule > 0 && <li>{t('xEusu.ruleN', { n: k.rule })}</li>}
        {k.lastPrice > 0 && <li>{t('xEusu.lastPriceN', { n: k.lastPrice })}</li>}
        {!c.hasRemark && <li className="muted">{t('xEusu.noRemarkCol')}</li>}
      </ul>
      {c.rows.length > 0 && (
        <table className="pa-table eu-tt">
          <thead><tr><th>{t('xEusu.itemCode')}</th><th>{t('xEusu.product')}</th><th>PO</th><th className="num">{t('xEusu.qty')}</th><th>{t('xEusu.issue')}</th></tr></thead>
          <tbody>
            {c.rows.map((r, i) => (
              <tr key={`${r.itemCode}-${i}`}><td className="eu-code">{r.itemCode}</td><td>{r.name}</td><td>{r.po}</td><td className="num">{n0(r.qty)}</td>
                <td>{r.flags.map((f) => t(`xEusu.flag_${f}`)).join(' · ')}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {c.rows.length > 0 && <p className="eu-sub">{t('xEusu.fixAfter')}</p>}
    </div>
  );
}

/* THE LINES THAT STILL NEED A PERSON (Max, 29/10: an item code that may be wrong → "cảnh báo để user update"; a root cause
   the PO does not give → "cảnh báo để user tự nhập"). An editor fixes each in place: the SAP code (kept for later files),
   the root cause (remembered for this SKU + PO), the price. Readers see the list. */
function NeedsFix({ d, t, canEdit, causes, onSaved }) {
  const rows = d.rows.filter((r) => r.flags && r.flags.length);
  const [val, setVal] = useState({});
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  if (!rows.length) return null;
  const put = async (r, body, key) => {
    setBusy(key); setErr('');
    try {
      const res = await fetch(`${API}/line/${r.lineId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setVal((v) => { const n = { ...v }; delete n[key]; return n; });
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  const box = (r, f, type, ph) => {
    const key = `${r.lineId}:${f}`;
    const field = f === 'code' || f === 'name' ? 'sapCode' : f;
    return (
      <span className="eu-fix">
        <input type={type} value={val[key] ?? ''} placeholder={ph} list={f === 'cause' ? 'eu-causes' : undefined} step={type === 'number' ? 'any' : undefined}
          onChange={(e) => setVal((v) => ({ ...v, [key]: e.target.value }))} data-eu-fix={key} aria-label={`${t(`xEusu.flag_${f}`)} ${r.itemCode}`} />
        <button type="button" className="pa-btn primary" disabled={!String(val[key] ?? '').trim() || busy === key}
          onClick={() => put(r, { [field]: type === 'number' ? Number(val[key]) : val[key] }, key)} data-eu-fixsave={key}>{t('xEusu.fixSave')}</button>
      </span>
    );
  };
  return (
    <div className="pa-section eu-needs" data-eu-needs>
      <h4>⚠ {t('xEusu.needsTitle', { n: rows.length })}</h4>
      <p className="eu-sub">{canEdit ? t('xEusu.needsSub') : t('xEusu.needsSubRead')}</p>
      {err && <div className="eu-flash bad">{err}</div>}
      <datalist id="eu-causes">{causes.filter((c) => c !== 'Unknown').map((c) => <option key={c} value={c} />)}</datalist>
      <div className="iv-wrap">
        <table className="pa-table eu-tt">
          <thead><tr><th>{t('xEusu.itemCode')}</th><th>{t('xEusu.product')}</th><th>PO</th><th className="num">{t('xEusu.qty')}</th><th>{t('xEusu.issue')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lineId} data-eu-line={r.lineId}>
                <td className="eu-code">{r.itemCode || r.sku}</td><td>{r.name}</td><td>{r.po || '—'}</td><td className="num">{n0(r.qty)}</td>
                <td className="eu-issues">
                  {r.flags.map((f) => (
                    <div key={f} className="eu-issue">
                      <b>{t(`xEusu.flag_${f}`)}</b>
                      {f === 'name' && <span className="eu-sub"> — {t('xEusu.nameIs', { name: r.masterName, sap: r.selling })}</span>}
                      {canEdit && f === 'code' && box(r, f, 'text', t('xEusu.sapPh'))}
                      {canEdit && f === 'name' && (
                        <>
                          {box(r, f, 'text', t('xEusu.sapPh'))}
                          <button type="button" className="pa-btn" disabled={busy === `${r.lineId}:ok`} onClick={() => put(r, { sapCode: r.selling }, `${r.lineId}:ok`)} data-eu-nameok={r.lineId}>{t('xEusu.nameOk')}</button>
                        </>
                      )}
                      {canEdit && f === 'cause' && box(r, f, 'text', t('xEusu.causePh'))}
                      {canEdit && f === 'price' && box(r, f, 'number', 'USD')}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ByCause({ d, t, colorOf }) {
  const [cause, setCause] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'amount', dir: -1 });
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return sortBy(d.rows.filter((r) => (!cause || (r.cause || 'Unknown') === cause)
      && (!s || r.sku.toLowerCase().includes(s) || String(r.name).toLowerCase().includes(s))), sort.key, sort.dir);
  }, [d, cause, q, sort]);
  return (
    <div className="pa-section">
      <div className="eu-grid2">
        <div className="eu-card"><h4>{t('xEusu.qtyByCause')}</h4><CauseBars rows={d.causes} valueKey="qty" colorOf={colorOf} fmt={n0} /></div>
        <div className="eu-card"><h4>{t('xEusu.amtByCause')}</h4><CauseBars rows={d.causes} valueKey="amount" colorOf={colorOf} fmt={usd0} /></div>
      </div>
      <div className="eu-chips">
        <button type="button" className={`eu-chip${!cause ? ' on' : ''}`} onClick={() => setCause('')}>{t('xEusu.all')} <em>{d.rows.length}</em></button>
        {d.causes.map((c) => (
          <button key={c.cause} type="button" className={`eu-chip${cause === c.cause ? ' on' : ''}`} onClick={() => setCause(c.cause)}>
            <i className="eu-sw" style={{ background: colorOf(c.cause) }} />{c.cause} <em>{c.skus}</em>
          </button>
        ))}
      </div>
      <div className="eu-card">
        <div className="eu-cardhead"><h4>{t('xEusu.skuDetail')} · {cause || t('xEusu.all')} <span className="eu-n">{t('xEusu.nRows', { n: shown.length })}</span></h4>
          <input className="eu-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xEusu.search')} aria-label={t('xEusu.search')} /></div>
        <div className="iv-wrap">
          <table className="pa-table eu-tt">
            <thead><tr>
              <SortTh k="sku" label={t('xEusu.sku')} sort={sort} setSort={setSort} />
              <SortTh k="name" label={t('xEusu.product')} sort={sort} setSort={setSort} />
              {!cause && <SortTh k="cause" label={t('xEusu.cause')} sort={sort} setSort={setSort} />}
              <SortTh k="collection" label={t('xEusu.collection')} sort={sort} setSort={setSort} />
              <SortTh k="vendor" label={t('xEusu.vendor')} sort={sort} setSort={setSort} />
              <SortTh k="qty" label={t('xEusu.qty')} sort={sort} setSort={setSort} num />
              <SortTh k="amount" label={t('xEusu.amount')} sort={sort} setSort={setSort} num />
              <SortTh k="t1Num" label={t('xEusu.t1')} sort={sort} setSort={setSort} num />
              <th>{t('xEusu.remark')}</th>
            </tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={`${r.sku}-${i}`}>
                  <td className="eu-code">{r.sku}</td><td>{r.name}</td>
                  {!cause && <td><i className="eu-sw" style={{ background: colorOf(r.cause || 'Unknown') }} />{r.cause || '—'}</td>}
                  <td>{r.collection || '—'}</td><td>{r.vendor || '—'}</td>
                  <td className="num">{n0(r.qty)}</td><td className="num">{usd(r.amount)}</td>
                  <td className="num">{r.t1Num ? usd(r.t1Num) : (r.t1 || '—')}</td><td className="eu-remark">{r.remark}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* THE DASHBOARD'S OWN WoW PAGE (Max, 2026-10-26, beside its screenshot: "tab này khác nhau nè"): four tiles — total
   qty change, total amount change, SKUs that rose, SKUs that fell — the table without a vendor column, cause and
   collection as tags. When the week before is identical, the tiles and the table speak of the most recent real change
   ("vs 09/25"), as build_dashboard.py's banner does. */
const COLL_TONE = { outdoor: 'g', tactical: 'r', home: 'b', play: 'p', special: 'n' };
const Tag = ({ text, color, tone }) => (
  text ? <span className={`eu-tag${tone ? ` eu-tag-${tone}` : ''}`} style={color ? { '--tag': color } : undefined}>{text}</span> : <span className="eu-nc">—</span>
);
function Wow({ d, t, colorOf }) {
  const [sort, setSort] = useState({ key: 'deltaAmt', dir: -1 });
  const lc = d.lastChange;
  const basis = d.wow.length
    ? { rows: d.wow, vs: d.prevLabel, qty: d.wowQty, amount: d.wowAmount }
    : (lc ? { rows: lc.rows, vs: label(lc.from), qty: lc.qty, amount: lc.amount } : null);
  const list = basis ? basis.rows : [];
  /* By the SIZE of the change, as the script lists it — a fall of 10,000 matters as much as a rise. */
  const rows = useMemo(() => [...list].sort((a, b) => (Math.abs(b[sort.key]) - Math.abs(a[sort.key])) * (sort.dir < 0 ? 1 : -1)), [list, sort]);
  const dirOf = (r) => (r.deltaQty || r.deltaAmt);
  const up = list.filter((r) => dirOf(r) > 0).length;
  const down = list.filter((r) => dirOf(r) < 0).length;
  return (
    <div className="pa-section">
      {!d.prevDate && <div className="eu-banner">{t('xEusu.firstWeek')}</div>}
      {d.prevDate && !d.wow.length && (
        <div className="eu-banner">{lc
          ? t('xEusu.lastChange', { p: d.prevLabel, d: d.label, from: label(lc.from), to: label(lc.to), q: signed(lc.qty, n0), a: signed(lc.amount, usd0) })
          : t('xEusu.noChange', { p: d.prevLabel, d: d.label })}</div>
      )}
      {basis && (
        <div className="iv-tiles">
          <div className="iv-tile"><span>{t('xEusu.kQtyChange')}</span><b><Delta v={basis.qty} f={n0} /></b><em>{t('xEusu.vsPrev', { d: basis.vs })}</em></div>
          <div className="iv-tile"><span>{t('xEusu.kAmtChange')}</span><b><Delta v={basis.amount} f={usdK} /></b><em>{t('xEusu.vsPrev', { d: basis.vs })}</em></div>
          <div className="iv-tile"><span>{t('xEusu.kUp')}</span><b className={up ? 'eu-upn' : ''}>{up}</b><em>{t('xEusu.vsPrev', { d: basis.vs })}</em></div>
          <div className="iv-tile"><span>{t('xEusu.kDown')}</span><b className={down ? 'eu-dnn' : ''}>{down}</b><em>{t('xEusu.vsPrev', { d: basis.vs })}</em></div>
        </div>
      )}
      {list.length > 0 && (
        <div className="eu-card">
          <h4>{t('xEusu.changedAt', { d: d.label, vs: basis.vs })} <span className="eu-n">({list.length})</span></h4>
          <p className="eu-sub">{t('xEusu.keyNote')}</p>
          <div className="iv-wrap">
            <table className="pa-table eu-tt">
              <thead><tr>
                <th>{t('xEusu.sku')}</th><th>{t('xEusu.product')}</th><th>{t('xEusu.cause')}</th><th>{t('xEusu.collection')}</th>
                <SortTh k="deltaQty" label={t('xEusu.dQty')} sort={sort} setSort={setSort} num />
                <SortTh k="deltaAmt" label={t('xEusu.dAmt')} sort={sort} setSort={setSort} num />
                <th className="num">{t('xEusu.curQty')}</th><th className="num">{t('xEusu.curAmt')}</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.sku}-${i}`}>
                    <td className="eu-code">{r.sku}</td><td>{r.name}</td>
                    <td><Tag text={r.cause} color={colorOf(r.cause || 'Unknown')} /></td>
                    <td><Tag text={r.collection} tone={COLL_TONE[String(r.collection || '').toLowerCase()] || 'n'} /></td>
                    <td className="num"><Delta v={r.deltaQty} f={n0} /></td><td className="num"><Delta v={r.deltaAmt} f={usd} /></td>
                    <td className="num">{n0(r.qty)}</td><td className="num">{usd(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
